import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, DollarSign, CreditCard, MapPin, Languages, Save, FlaskConical, Image as ImageIcon, ShieldCheck } from 'lucide-react';
import { adminSettings as defaultSettings, paymentGateways } from '../config';
import { toast } from '../hooks/use-toast';
import AdminCompareSettings from '../components/AdminCompareSettings';
import AdminMapSettings from '../components/AdminMapSettings';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// --- Image Upload Limit card ---
const AdminImageLimitCard = () => {
  const [val, setVal] = useState(9);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/settings/image-limit`)
      .then(r => setVal(r.data?.max_images_per_listing || 9))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const r = await axios.put(
        `${API_URL}/api/admin/settings/image-limit`,
        { max_images_per_listing: parseInt(val, 10) },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setVal(r.data.max_images_per_listing);
      toast({ title: 'Image limit updated', description: `New limit: ${r.data.max_images_per_listing} per listing.` });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <ImageIcon className="h-5 w-5 text-purple-600" />
          <CardTitle>Image Upload Limit</CardTitle>
        </div>
        <CardDescription>
          Set how many images (1-20) a user can upload per listing. Applies to all
          categories and all free customers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Label htmlFor="max-img-input" className="font-semibold whitespace-nowrap">
            Max images per listing
          </Label>
          <Input
            id="max-img-input"
            type="number"
            min={1}
            max={20}
            value={val}
            disabled={!loaded || saving}
            onChange={(e) => setVal(e.target.value)}
            className="w-24"
            data-testid="admin-input-max-images"
          />
          <Button
            onClick={save}
            disabled={!loaded || saving}
            size="sm"
            data-testid="admin-save-max-images"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Currently: <span className="font-semibold">{val}</span> images allowed
          per ad. Existing ads are unaffected; new uploads honour this value.
        </p>
      </CardContent>
    </Card>
  );
};

// --- Auth Policy (SMS signup / phone-verify guard / forgot-password) card ---
const AdminAuthSettingsCard = () => {
  const DEFAULTS = {
    signup_sms_required: true,
    signup_email_verification_required: false,
    phone_verify_grace_hours: 24,
    phone_verify_guard_enabled: true,
    phone_verify_guard_message: 'Please verify your phone number by SMS before posting your first ad.',
    password_reset_sms_enabled: true,
    password_reset_email_enabled: true,
    sms_rate_limit_max_per_window: 2,
    sms_rate_limit_window_sec: 300,
    otp_max_attempts: 5,
    otp_code_ttl_min: 10,
    signup_session_ttl_min: 30,
  };

  const [cfg, setCfg] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/auth-settings`)
      .then((r) => setCfg({ ...DEFAULTS, ...(r.data || {}) }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const set = (k, v) => setCfg((prev) => ({ ...prev, [k]: v }));
  const num = (k, v, min, max) => {
    let n = parseInt(v, 10);
    if (Number.isNaN(n)) n = min;
    n = Math.max(min, Math.min(max, n));
    set(k, n);
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const { data } = await axios.put(
        `${API_URL}/api/admin/auth-settings`,
        cfg,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (data?.auth_settings) setCfg({ ...DEFAULTS, ...data.auth_settings });
      toast({ title: 'Auth policy saved', description: 'Signup, phone-verify and reset rules updated.' });
    } catch (e) {
      toast({
        title: 'Failed to save auth settings',
        description: e?.response?.data?.detail || 'Try again - admin login may be required.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="lg:col-span-2" data-testid="admin-auth-settings-card">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <CardTitle>Auth &amp; Verification Policy</CardTitle>
        </div>
        <CardDescription>
          Control how strictly signup, phone verification and password reset are enforced.
          Changes apply immediately to new signups and reset attempts.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ---------- Signup section ---------- */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Signup</h4>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-base font-semibold">Require SMS OTP at signup</Label>
              <p className="text-sm text-gray-500">
                When ON, every new user must verify their phone by SMS before an account is created.
              </p>
            </div>
            <Switch
              checked={!!cfg.signup_sms_required}
              disabled={!loaded || saving}
              onCheckedChange={(v) => set('signup_sms_required', v)}
              data-testid="admin-auth-toggle-signup-sms-required"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-base font-semibold">Also require email verification</Label>
              <p className="text-sm text-gray-500">
                When ON, users must additionally verify their email address to complete signup.
              </p>
            </div>
            <Switch
              checked={!!cfg.signup_email_verification_required}
              disabled={!loaded || saving}
              onCheckedChange={(v) => set('signup_email_verification_required', v)}
              data-testid="admin-auth-toggle-signup-email-required"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="signup-session-ttl">Signup session TTL (minutes, 5-180)</Label>
              <Input
                id="signup-session-ttl"
                type="number"
                min={5}
                max={180}
                value={cfg.signup_session_ttl_min}
                disabled={!loaded || saving}
                onChange={(e) => num('signup_session_ttl_min', e.target.value, 5, 180)}
                data-testid="admin-auth-input-signup-session-ttl"
              />
              <p className="text-xs text-gray-500 mt-1">How long a half-finished signup wizard stays valid.</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* ---------- Phone-verify guard (post-email-signup) ---------- */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Phone-verify guard (email-signup users)</h4>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-base font-semibold">Enforce phone verification before posting ads</Label>
              <p className="text-sm text-gray-500">
                When ON, users who signed up via email must verify phone by SMS before their first listing.
                Turn OFF only in low-fraud environments.
              </p>
            </div>
            <Switch
              checked={!!cfg.phone_verify_guard_enabled}
              disabled={!loaded || saving}
              onCheckedChange={(v) => set('phone_verify_guard_enabled', v)}
              data-testid="admin-auth-toggle-guard-enabled"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="grace-hours">Grace window (hours, 1-168)</Label>
              <Input
                id="grace-hours"
                type="number"
                min={1}
                max={168}
                value={cfg.phone_verify_grace_hours}
                disabled={!loaded || saving}
                onChange={(e) => num('phone_verify_grace_hours', e.target.value, 1, 168)}
                data-testid="admin-auth-input-grace-hours"
              />
              <p className="text-xs text-gray-500 mt-1">How long email-signup users may browse before phone verification is required.</p>
            </div>
          </div>

          <div>
            <Label htmlFor="guard-msg">Guard block message</Label>
            <Input
              id="guard-msg"
              value={cfg.phone_verify_guard_message}
              disabled={!loaded || saving}
              onChange={(e) => set('phone_verify_guard_message', e.target.value)}
              data-testid="admin-auth-input-guard-msg"
            />
            <p className="text-xs text-gray-500 mt-1">Shown when a user tries to post an ad without a verified phone.</p>
          </div>
        </div>

        <Separator />

        {/* ---------- Password reset channels ---------- */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Password reset</h4>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-base font-semibold">Allow SMS-based password reset</Label>
              <p className="text-sm text-gray-500">Users can request a reset OTP to their verified phone.</p>
            </div>
            <Switch
              checked={!!cfg.password_reset_sms_enabled}
              disabled={!loaded || saving}
              onCheckedChange={(v) => set('password_reset_sms_enabled', v)}
              data-testid="admin-auth-toggle-reset-sms-enabled"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="pr-4">
              <Label className="text-base font-semibold">Allow email-based password reset</Label>
              <p className="text-sm text-gray-500">Users can request a reset link/OTP to their email.</p>
            </div>
            <Switch
              checked={!!cfg.password_reset_email_enabled}
              disabled={!loaded || saving}
              onCheckedChange={(v) => set('password_reset_email_enabled', v)}
              data-testid="admin-auth-toggle-reset-email-enabled"
            />
          </div>

          {!cfg.password_reset_sms_enabled && !cfg.password_reset_email_enabled && (
            <Badge variant="destructive" data-testid="admin-auth-reset-warn">
              At least one reset channel must be enabled - users cannot reset their password otherwise.
            </Badge>
          )}
        </div>

        <Separator />

        {/* ---------- OTP & rate-limit knobs ---------- */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">OTP &amp; rate limits</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="otp-ttl">OTP code TTL (minutes, 2-60)</Label>
              <Input
                id="otp-ttl"
                type="number"
                min={2}
                max={60}
                value={cfg.otp_code_ttl_min}
                disabled={!loaded || saving}
                onChange={(e) => num('otp_code_ttl_min', e.target.value, 2, 60)}
                data-testid="admin-auth-input-otp-ttl"
              />
            </div>
            <div>
              <Label htmlFor="otp-attempts">Max verify attempts per code (1-20)</Label>
              <Input
                id="otp-attempts"
                type="number"
                min={1}
                max={20}
                value={cfg.otp_max_attempts}
                disabled={!loaded || saving}
                onChange={(e) => num('otp_max_attempts', e.target.value, 1, 20)}
                data-testid="admin-auth-input-otp-attempts"
              />
            </div>
            <div>
              <Label htmlFor="sms-max">SMS sends allowed per window (1-20)</Label>
              <Input
                id="sms-max"
                type="number"
                min={1}
                max={20}
                value={cfg.sms_rate_limit_max_per_window}
                disabled={!loaded || saving}
                onChange={(e) => num('sms_rate_limit_max_per_window', e.target.value, 1, 20)}
                data-testid="admin-auth-input-sms-max"
              />
            </div>
            <div>
              <Label htmlFor="sms-window">Rate-limit window (seconds, 60-3600)</Label>
              <Input
                id="sms-window"
                type="number"
                min={60}
                max={3600}
                value={cfg.sms_rate_limit_window_sec}
                disabled={!loaded || saving}
                onChange={(e) => num('sms_rate_limit_window_sec', e.target.value, 60, 3600)}
                data-testid="admin-auth-input-sms-window"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={save}
            disabled={!loaded || saving || (!cfg.password_reset_sms_enabled && !cfg.password_reset_email_enabled)}
            data-testid="admin-auth-save-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving…' : 'Save auth policy'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


const AdminDemoListingsCard = () => {
  const { formatCurrency } = useCountry();
  const [hide, setHide] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visRows, setVisRows] = useState([]);
  const [visLoading, setVisLoading] = useState(true);
  const [visSaving, setVisSaving] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/settings/demo`);
      setHide(!!data.hide_demo_listings);
      setCount(data.demo_listings_count || 0);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadVisibility = async () => {
    setVisLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/settings/demo-visibility`);
      setVisRows(data.categories || []);
    } catch (e) {
      // silent
    } finally {
      setVisLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadVisibility();
  }, []);

  const setCategoryMode = async (parent_category, mode) => {
    setVisSaving(parent_category);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/settings/demo-visibility`,
        { parent_category, mode },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      // Optimistic + reload so demos_visible reflects the new mode
      await loadVisibility();
      toast({
        title: 'Demo visibility updated',
        description: `${parent_category}: ${mode.replace('_', ' ')}`,
      });
    } catch (e) {
      toast({
        title: 'Failed to update category',
        description: e?.response?.data?.detail || 'Try again later.',
        variant: 'destructive',
      });
    } finally {
      setVisSaving('');
    }
  };

  const onToggle = async (next) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/settings/demo`,
        { hide_demo_listings: next },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setHide(next);
      toast({
        title: next ? 'Demo listings hidden' : 'Demo listings visible',
        description: next
          ? 'Global kill switch - every category now hides demos.'
          : 'Global kill switch OFF - per-category rules apply again.',
      });
    } catch (e) {
      toast({
        title: 'Failed to update',
        description: e?.response?.data?.detail || 'Try again later.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="lg:col-span-2" data-testid="admin-demo-listings-card">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <FlaskConical className="h-5 w-5 text-amber-600" />
          <CardTitle>Demo Listings</CardTitle>
        </div>
        <CardDescription>
          Manage soft-launch demo (dummy) listings. Each category can be set to auto-hide
          demos as soon as one real listing goes live, always show, or always hide.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base font-semibold">Global kill switch - hide ALL demos</Label>
            <p className="text-sm text-gray-500">
              When ON, overrides every per-category rule below and hides all demo listings.
            </p>
          </div>
          <Switch
            checked={hide}
            disabled={loading || saving}
            onCheckedChange={onToggle}
            data-testid="admin-toggle-hide-demo"
          />
        </div>
        <Separator />

        {/* Per-parent-category visibility control */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-base font-semibold">Per-category demo visibility</Label>
            <Badge variant="outline" className="text-xs">
              {visLoading ? '…' : `${visRows.length} categories`}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            <strong>Auto</strong> hides demos in a category the moment a real listing is
            published there. <strong>Always show</strong> keeps demos visible even after
            real listings arrive. <strong>Always hide</strong> hides demos immediately.
          </p>
          <div className="border rounded-lg divide-y">
            <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-600">
              <div className="col-span-4">Category</div>
              <div className="col-span-2 text-center">Real</div>
              <div className="col-span-2 text-center">Demos</div>
              <div className="col-span-2 text-center">Status</div>
              <div className="col-span-2 text-right">Mode</div>
            </div>
            {visLoading && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">Loading…</div>
            )}
            {!visLoading && visRows.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No parent categories found.</div>
            )}
            {visRows.map((row) => {
              const isBusy = visSaving === row.parent_category;
              const label = row.parent_category
                .split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              return (
                <div key={row.parent_category} className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-sm hover:bg-gray-50" data-testid={`demo-vis-row-${row.parent_category}`}>
                  <div className="col-span-4 font-medium text-gray-800 truncate">{label}</div>
                  <div className="col-span-2 text-center">
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                      {row.real_count}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-center">
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                      {row.demo_count}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-center">
                    {row.demos_visible ? (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">Visible</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">Hidden</Badge>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={row.mode}
                      disabled={isBusy || hide}
                      onValueChange={(v) => setCategoryMode(row.parent_category, v)}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`demo-vis-select-${row.parent_category}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="force_show">Always show</SelectItem>
                        <SelectItem value="force_hide">Always hide</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
          {hide && (
            <p className="text-xs text-amber-700 mt-2 italic">
              Per-category rules are inactive while the global kill switch is ON.
            </p>
          )}
        </div>

        <Separator />
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Demo listings in DB:</span>
          <Badge variant="secondary" data-testid="admin-demo-count">
            {loading ? '…' : count}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminSettings = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCountry();
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Guard: only admins may view this page
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login', { replace: true });
      return;
    }
    setAuthChecked(true);
  }, [navigate]);

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  const handleNumberChange = (key, value) => {
    setSettings({ ...settings, [key]: parseFloat(value) || 0 });
  };

  const handleGatewayToggle = (gatewayId) => {
    setSettings({
      ...settings,
      payment_gateways: {
        ...settings.payment_gateways,
        [`${gatewayId}_enabled`]: !settings.payment_gateways[`${gatewayId}_enabled`]
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    // Mock save - replace with actual API call
    setTimeout(() => {
      localStorage.setItem('admin_settings', JSON.stringify(settings));
      setSaving(false);
      toast({
        title: "Settings Saved",
        description: "Your configuration has been updated successfully.",
      });
    }, 1000);
  };

  useEffect(() => {
    // Load saved settings from localStorage
    const saved = localStorage.getItem('admin_settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);

  // Block render until admin auth verified - prevents flicker + satisfies E2E auth-guard tests
  if (!authChecked) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center space-x-3">
            <Settings className="h-8 w-8" />
            <div>
              <h1 className="text-3xl font-bold">Admin Settings</h1>
              <p className="text-blue-100">Configure pricing, payments, and features</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pricing Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <CardTitle>Pricing Configuration</CardTitle>
              </div>
              <CardDescription>Set ad posting fees and premium pricing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Normal Ads */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Paid Normal Ads</Label>
                    <p className="text-sm text-gray-500">Charge users for posting normal ads</p>
                  </div>
                  <Switch
                    checked={settings.enable_paid_normal_ads}
                    onCheckedChange={() => handleToggle('enable_paid_normal_ads')}
                  />
                </div>
                {settings.enable_paid_normal_ads && (
                  <div>
                    <Label htmlFor="normal_ad_fee">Normal Ad Fee (Rs)</Label>
                    <Input
                      id="normal_ad_fee"
                      type="number"
                      value={settings.normal_ad_fee}
                      onChange={(e) => handleNumberChange('normal_ad_fee', e.target.value)}
                      placeholder="500"
                    />
                    <p className="text-sm text-gray-500 mt-1">Current: {formatCurrency(settings.normal_ad_fee)}</p>
                  </div>
                )}
                {!settings.enable_paid_normal_ads && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    Normal ads are FREE
                  </Badge>
                )}
              </div>

              <Separator />

              {/* Premium Ads */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Premium Ads</Label>
                    <p className="text-sm text-gray-500">Enable premium ad upgrades</p>
                  </div>
                  <Switch
                    checked={settings.premium_ad_enabled}
                    onCheckedChange={() => handleToggle('premium_ad_enabled')}
                  />
                </div>
                {settings.premium_ad_enabled && (
                  <>
                    <div>
                      <Label htmlFor="premium_ad_price">Premium Ad Price (Rs)</Label>
                      <Input
                        id="premium_ad_price"
                        type="number"
                        value={settings.premium_ad_price}
                        onChange={(e) => handleNumberChange('premium_ad_price', e.target.value)}
                        placeholder="999"
                      />
                      <p className="text-sm text-gray-500 mt-1">Current: {formatCurrency(settings.premium_ad_price)}</p>
                    </div>
                    <div>
                      <Label htmlFor="premium_ad_duration">Premium Duration (Days)</Label>
                      <Input
                        id="premium_ad_duration"
                        type="number"
                        value={settings.premium_ad_duration}
                        onChange={(e) => handleNumberChange('premium_ad_duration', e.target.value)}
                        placeholder="90"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Current: {settings.premium_ad_duration} days (~{Math.floor(settings.premium_ad_duration / 30)} months)
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Gateways */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5 text-blue-600" />
                <CardTitle>Payment Gateways</CardTitle>
              </div>
              <CardDescription>Enable/disable payment methods</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentGateways.map((gateway) => (
                <div key={gateway.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Label className="font-semibold">{gateway.name}</Label>
                      <Badge variant="outline" className="text-xs">
                        {gateway.type === 'local' ? '🇱🇰 Local' : '🌍 International'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{gateway.description}</p>
                  </div>
                  <Switch
                    checked={settings.payment_gateways[`${gateway.id}_enabled`]}
                    onCheckedChange={() => handleGatewayToggle(gateway.id)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Feature Toggles */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <CardTitle>Advanced Features</CardTitle>
              </div>
              <CardDescription>Location & language enhancements</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg bg-green-50">
                <div className="flex items-center space-x-2 mb-2">
                  <MapPin className="h-5 w-5 text-green-600" />
                  <Label className="font-semibold text-green-900">Map-Based Location</Label>
                  <Badge className="bg-green-600">Active</Badge>
                </div>
                <p className="text-sm text-green-700">
                  Users can pin exact location on map with lat/long coordinates
                </p>
              </div>

              <div className="p-4 border rounded-lg bg-blue-50">
                <div className="flex items-center space-x-2 mb-2">
                  <Languages className="h-5 w-5 text-blue-600" />
                  <Label className="font-semibold text-blue-900">Language Enhancement</Label>
                  <Badge className="bg-blue-600">Active</Badge>
                </div>
                <p className="text-sm text-blue-700">
                  AI-powered text refinement for English & Sinhala with spell check
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Property Compare Settings */}
          <AdminCompareSettings />

          {/* Map Feature Settings (Hover-to-Zoom) */}
          <AdminMapSettings />

          {/* Auth policy - signup, phone-verify guard, forgot-password rules */}
          <AdminAuthSettingsCard />

          {/* Demo Listings - soft-launch dummy ad management */}
          <AdminDemoListingsCard />

          {/* Image Upload Limit - admin-configurable per-listing image count */}
          <AdminImageLimitCard />

          {/* Current Configuration Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Current Configuration</CardTitle>
              <CardDescription>Active pricing and features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Normal Ad Posting:</span>
                <Badge variant={settings.enable_paid_normal_ads ? 'default' : 'secondary'}>
                  {settings.enable_paid_normal_ads ? formatCurrency(settings.normal_ad_fee) : 'FREE'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Premium Ad Price:</span>
                <Badge variant={settings.premium_ad_enabled ? 'default' : 'secondary'}>
                  {settings.premium_ad_enabled ? formatCurrency(settings.premium_ad_price) : 'Disabled'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Premium Duration:</span>
                <span className="text-sm">{settings.premium_ad_duration} days</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Active Payment Gateways:</span>
                <span className="text-sm font-semibold">
                  {Object.values(settings.payment_gateways).filter(Boolean).length} / {paymentGateways.length}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            size="lg"
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;